/**
 * BulkReclassifyDialog - bulk re-categorise N selected bank-feed
 * transactions to a single GL category.
 *
 * Safety notes for non-accountants:
 *   • Pending rows just stage the new GL (next approval picks it up).
 *   • Completed rows trigger a reversal of the existing journal and a
 *     re-post of a new one - the bank balance never moves, but the audit
 *     trail records both events. We surface this explicitly so the user
 *     understands what's about to happen.
 *
 * The dialog uses <ConfirmDestructive> semantics for the warning when any
 * selected row is completed (because journals will be reversed).
 */
import React, { useMemo, useState } from 'react';
import { glToFamily } from '../../utils/glToFamily';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { GLAccountSearchableSelect } from '../common/GLAccountSelector';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Loader2, Tags, AlertTriangle, RotateCcw, Sparkles } from 'lucide-react';
import { formatCurrency } from '../../utils/currency';

const groupAccountsByType = (accounts) => {
  const groups = { revenue: [], expense: [], asset: [], liability: [], equity: [], other: [] };
  accounts.forEach((a) => {
    const t = (a.type || a.account_type || 'other').toLowerCase();
    if (groups[t]) groups[t].push(a);
    else groups.other.push(a);
  });
  return groups;
};

const TYPE_LABEL = {
  revenue: 'Revenue / Income',
  expense: 'Expenses',
  asset: 'Assets',
  liability: 'Liabilities',
  equity: 'Equity',
  other: 'Other',
};

export const BulkReclassifyDialog = ({
  open,
  onOpenChange,
  selectedTransactions = [], // array of full tx objects
  glAccounts = [],
  currency = 'NGN',
  onConfirm,                // async ({ gl_code, reason }) => result
}) => {
  const [glCode, setGlCode] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(null); // { succeeded, failed } when finished

  const groups = useMemo(() => groupAccountsByType(glAccounts), [glAccounts]);

  const stats = useMemo(() => {
    // "Completed" (journal reversal + re-post) is decided EXACTLY like the backend (/bulk-reclassify): by status.
    // `processed_at` / `is_processed` are stamped when the AI pipeline classified the row, not when it was posted —
    // using them made Review-needed rows show up as "In books" (Oct 2026 report).
    const isCompleted = (t) => ['completed', 'processed', 'matched', 'created', 'reconciled'].includes(t.status);
    const completed = selectedTransactions.filter(isCompleted).length;
    const pending = selectedTransactions.length - completed;
    const totalAmount = selectedTransactions.reduce(
      (sum, t) => sum + Math.abs(t.amount || 0),
      0,
    );
    return { pending, completed, totalAmount };
  }, [selectedTransactions]);

  const targetAccount = glAccounts.find((a) => a.code === glCode);
  const SPECIAL = ['fixed_asset', 'prepayment', 'loan', 'loan_received', 'investment', 'loan_receivable', 'deferred_revenue'];
  const specialFamily = glCode
    ? SPECIAL.find((f) => selectedTransactions.some((t) => glToFamily(glCode, t.direction, glAccounts) === f)) || null
    : null;

  const handleSubmit = async () => {
    if (!glCode) return;
    setSubmitting(true);
    setProgress(null);
    try {
      const result = await onConfirm?.({ gl_code: glCode, reason });
      setProgress({
        succeeded: result?.succeeded_count ?? 0,
        failed: result?.failed_count ?? 0,
        failures: result?.failed || [],
      });
      // Auto-close after a successful all-pass run
      if ((result?.failed_count ?? 0) === 0) {
        setTimeout(() => {
          handleClose(true);
        }, 1200);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = (force = false) => {
    if (submitting && !force) return;
    setGlCode('');
    setReason('');
    setProgress(null);
    onOpenChange?.(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg" data-testid="bulk-reclassify-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tags className="w-5 h-5 text-[#108981]" />
            Bulk Re-classify {selectedTransactions.length} transaction{selectedTransactions.length === 1 ? '' : 's'}
          </DialogTitle>
          <DialogDescription>
            Apply a single category to every selected transaction. Bank balances aren't affected: only the category each line posts to.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Selection summary */}
          <div className="grid grid-cols-3 gap-2 text-center text-xs">
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-2">
              <p className="font-semibold text-amber-700">{stats.pending}</p>
              <p className="text-amber-600">Review needed</p>
            </div>
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-2">
              <p className="font-semibold text-emerald-700">{stats.completed}</p>
              <p className="text-emerald-600">In Books</p>
            </div>
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-2">
              <p className="font-semibold text-slate-700 tabular-nums">{formatCurrency(stats.totalAmount, currency)}</p>
              <p className="text-slate-500">Total volume</p>
            </div>
          </div>

          {/* Completed warning */}
          {stats.completed > 0 && !progress && (
            <Alert className="border-amber-300 bg-amber-50">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              <AlertDescription className="text-xs text-amber-800">
                <p className="font-semibold mb-1">
                  <RotateCcw className="w-3 h-3 inline mr-1" />
                  {stats.completed} transaction{stats.completed === 1 ? '' : 's'} already in your books will be re-posted
                </p>
                For each, the original journal entry is <strong>reversed</strong> and a new one is <strong>posted</strong> with the new category. Both events are recorded in the audit trail. The bank balance stays the same.
              </AlertDescription>
            </Alert>
          )}

          {/* Target category */}
          {!progress && (
            <div className="space-y-1.5">
              <Label htmlFor="bulk-gl-select" className="text-sm font-medium">
                New category
              </Label>
              <GLAccountSearchableSelect
                value={glCode}
                onChange={setGlCode}
                placeholder="Choose a category…"
                triggerTestId="bulk-gl-select"
              />
              {targetAccount && !specialFamily && (
                <p className="text-xs text-muted-foreground">
                  All {selectedTransactions.length} transactions will post to{' '}
                  <Badge variant="outline" className="text-[10px]">{targetAccount.name}</Badge>.
                </p>
              )}
              {specialFamily && (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2" data-testid="bulk-reclassify-special-warning">
                  <strong>{targetAccount?.name || glCode}</strong> is a special item ({specialFamily.replace(/_/g, ' ')}). Bulk re-classify can't apply it —
                  each transaction needs its own match-existing / track-as-new decision. Re-classify these rows one at a time.
                </p>
              )}
            </div>
          )}

          {/* Optional reason */}
          {!progress && (
            <div className="space-y-1.5">
              <Label htmlFor="bulk-reclassify-reason" className="text-sm font-medium">
                Reason <span className="text-muted-foreground">(optional, helps AI learn)</span>
              </Label>
              <Input
                id="bulk-reclassify-reason"
                data-testid="bulk-reclassify-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. supplier was misclassified by AI"
                maxLength={200}
              />
            </div>
          )}

          {/* AI learning callout */}
          {!progress && (
            <div className="flex items-start gap-2 rounded-lg bg-violet-50 border border-violet-200 p-2.5">
              <Sparkles className="w-4 h-4 text-violet-600 shrink-0 mt-0.5" />
              <p className="text-xs text-violet-700">
                Every correction trains AkountFly's matching engine. Similar future transactions will be auto-categorised correctly.
              </p>
            </div>
          )}

          {/* Result panel */}
          {progress && (
            <Alert className={`${progress.failed === 0 ? 'border-emerald-300 bg-emerald-50' : 'border-amber-300 bg-amber-50'}`}>
              <AlertDescription className="text-sm">
                <p className="font-semibold mb-1">
                  {progress.failed === 0
                    ? `✓ Re-classified ${progress.succeeded} transaction${progress.succeeded === 1 ? '' : 's'}.`
                    : `Re-classified ${progress.succeeded}; ${progress.failed} failed.`}
                </p>
                {progress.failed > 0 && (
                  <ul className="text-xs text-amber-800 list-disc ml-4 mt-1 max-h-32 overflow-auto">
                    {progress.failures.slice(0, 10).map((f, i) => (
                      <li key={i}>
                        <span className="font-mono">{f.id.slice(0, 8)}…</span>: {f.reason}
                      </li>
                    ))}
                  </ul>
                )}
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          {!progress ? (
            <>
              <Button
                variant="outline"
                onClick={() => handleClose()}
                disabled={submitting}
                data-testid="bulk-reclassify-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!glCode || !!specialFamily || submitting}
                className="bg-[#108981] hover:bg-[#0b6e68]"
                data-testid="bulk-reclassify-confirm"
              >
                {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}
                {submitting ? 'Re-classifying…' : `Re-classify ${selectedTransactions.length} transaction${selectedTransactions.length === 1 ? '' : 's'}`}
              </Button>
            </>
          ) : (
            <Button onClick={() => handleClose(true)} className="bg-[#108981] hover:bg-[#0b6e68]" data-testid="bulk-reclassify-done">
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkReclassifyDialog;
